import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { fmtDate as fmtMMDDYY } from '@/lib/dates';
import { ShieldAlert, Truck, Inbox } from 'lucide-react';
import { useOpsPurgeAccess, PurgeButton, PurgeReviewDialog } from '@/components/operations/hub/OpsPurge';
import { MobileRecordCard, MobileRecordList } from '@/components/common/mobile/MobileRecordCard';

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });
const fmtDate = (d) => fmtMMDDYY(d) || '—';

// Functional record lists that also carry the shared purge control. These are
// genuine record lists (they read and show real stored records) — NOT placeholder
// screens built solely to host a purge button.
const MODULES = {
  incidents: {
    entityType: 'incident',
    endpoint: '/incident-reports',
    title: 'Incidents',
    subtitle: 'Operational & safety incidents',
    icon: '/app_incidents_n.png',
    Icon: ShieldAlert,
    intro: 'Operational and safety incidents with people, customer and location involved, photographs, and required investigation and follow-up. A high/critical incident can generate corrective actions and follow-up tasks — those are owned by the incident and are removed with it on purge.',
    columns: [
      { key: 'report_number', label: 'Report #', mono: true },
      { key: 'incident_type', label: 'Type' },
      { key: 'location_of_incident', label: 'Location' },
      { key: 'reported_by', label: 'Reported By' },
      { key: 'date_occurred', label: 'Occurred', date: true },
    ],
  },
  vehicles: {
    entityType: 'vehicle_inspection',
    endpoint: '/vehicle-inspections',
    title: 'Vehicle Inspections',
    subtitle: 'Operational vehicle inspection records',
    icon: '/app_vehicles_n.png',
    Icon: Truck,
    intro: 'Vehicle inspection records captured in the field. The shared vehicle master (identity, VIN, tag) is owned by the Admin Hub and is NEVER deleted here — purge removes only the inspection record and its captured photos.',
    columns: [
      { key: 'inspection_number', label: 'Inspection #', mono: true },
      { key: 'location_of_inspection', label: 'Location' },
      { key: 'assigned_user', label: 'Inspector' },
      { key: 'date_of_inspection', label: 'Date', date: true },
      { key: 'status', label: 'Status', badge: true },
    ],
  },
};

const OperationsRecordList = ({ module }) => {
  const { token } = useContext(AuthContext);
  const canPurge = useOpsPurgeAccess();
  const cfg = MODULES[module] || MODULES.incidents;
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState([]);
  const [showPurge, setShowPurge] = useState(false);
  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const load = () => axios.get(`${API}${cfg.endpoint}`, auth(token))
    .then((r) => { setRows(Array.isArray(r.data) ? r.data : (r.data.items || [])); setSelected([]); })
    .catch(() => { setRows([]); });
  useEffect(() => { load(); }, [module, token]);

  const HeaderIcon = cfg.Icon;
  const colCount = cfg.columns.length + (canPurge ? 1 : 0);

  return (
    <div data-testid={`ops-records-${module}`}>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-4">
          <img src={cfg.icon} alt={cfg.title} className="h-12 w-12 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
          <div>
            <div className="text-sm font-bold uppercase tracking-[0.18em] text-[#8FC93A]">Operations Module</div>
            <h1 className="text-[28px] leading-tight font-extrabold tracking-tight text-[#0E2A47]">{cfg.title}</h1>
          </div>
        </div>
        {canPurge && <PurgeButton count={selected.length} onClick={() => setShowPurge(true)} />}
      </div>

      <p className="mb-4 max-w-3xl text-sm leading-relaxed text-slate-500">{cfg.intro}</p>

      {/* Desktop: table. Mobile/tablet: compact record cards. */}
      <div className="hidden lg:block overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid={`ops-records-table-${module}`}>
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              {canPurge && <th className="px-4 py-3 w-8"></th>}
              {cfg.columns.map((c) => <th key={c.key} className="px-4 py-3">{c.label}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(rows || []).map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`ops-records-row-${r.id}`}>
                {canPurge && <td className="px-4 py-2.5"><input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggleSel(r.id)} className="h-4 w-4 accent-red-600" data-testid={`ops-records-select-${r.id}`} /></td>}
                {cfg.columns.map((c) => (
                  <td key={c.key} className={`px-4 py-2.5 ${c.mono ? 'font-mono text-xs text-[#0E2A47]' : 'text-slate-600'}`}>
                    {c.badge
                      ? <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold text-slate-600">{r[c.key] || '—'}</span>
                      : (c.date ? fmtDate(r[c.key]) : (r[c.key] || '—'))}
                  </td>
                ))}
              </tr>
            ))}
            {rows && rows.length === 0 && (
              <tr><td colSpan={colCount} className="px-4 py-12 text-center text-slate-400">
                <Inbox size={26} className="mx-auto mb-2 text-slate-300" />No {cfg.title.toLowerCase()} on record yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="lg:hidden">
        <MobileRecordList testid={`ops-records-mobile-${module}`}>
          {(rows || []).map((r) => {
            const titleCol = cfg.columns.find((c) => c.mono) || cfg.columns[0];
            const badgeCol = cfg.columns.find((c) => c.badge);
            const metaCols = cfg.columns.filter((c) => c.key !== titleCol.key && (!badgeCol || c.key !== badgeCol.key));
            const fmtVal = (c) => (c.date ? fmtDate(r[c.key]) : (r[c.key] || '—'));
            return (
              <MobileRecordCard
                key={r.id}
                testid={`ops-records-card-${r.id}`}
                image={canPurge ? (
                  <div className="pt-0.5"><input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggleSel(r.id)} className="h-4 w-4 accent-red-600" data-testid={`ops-records-card-select-${r.id}`} /></div>
                ) : null}
                title={fmtVal(titleCol)}
                badge={badgeCol ? <span className="whitespace-nowrap rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold text-slate-600">{r[badgeCol.key] || '—'}</span> : null}
                meta={metaCols.map((c) => ({ label: c.label, value: fmtVal(c) }))}
              />
            );
          })}
        </MobileRecordList>
        {rows && rows.length === 0 && (
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-12 text-center text-slate-400">
            <Inbox size={26} className="mx-auto mb-2 text-slate-300" />No {cfg.title.toLowerCase()} on record yet.
          </div>
        )}
      </div>

      {showPurge && <PurgeReviewDialog entityType={cfg.entityType} ids={selected} onClose={() => setShowPurge(false)} onDone={() => { setShowPurge(false); load(); }} />}
    </div>
  );
};

export default OperationsRecordList;
